import React, { useEffect, useState } from "react";
// ANT DESIGN COMPONENTS
import { Drawer, Tabs } from "antd";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import {
  updateOpenModalControlPanelLift,
  updateLiftSelected,
  updateBankSelected,
} from "../../../redux/monitoring/monitoringSlice";
import { useLazyGetServerQuery } from "../../../redux/servers/serverAPI";
// COMPONENTS
import ControlPanelLiftHeader from "./ControlPanelLiftHeader";
import ControlPanelLiftElevator from "./ControlPanelLiftElevator";
import ControlPanelLiftFaults from "./ControlPanelLiftFaults";
import ControlPanelLiftStatus from "./ControlPanelLiftStatus";
import ControlPanelLiftActions from "./ControlPanelLiftActions";
import ControlPanelStateView from "./ControlPanelStateView";
import ButtonClose from "../../components/buttons/ButtonClose";
// INTERFACES
import { RootState } from "../../../app/store";
import { ServerInterface } from "../../../interfaces/Server.interface";
// AUTHORIZATION
import validatePermission from "../../../utils/validatePermissions";
import GETJwtToken from "../../../redux/authentication/authentication";
// CONSTANTS
import { BLANK_LIFT } from "../../../constants/lifts";
import { BLANK_BANK } from "../../../constants/bank";
import { BLANK_SERVER } from "../../../constants/server";

export default function ControlPanelLift() {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const dispatch = useDispatch();

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const {
    liftSelected,
    bankSelected,
    openModalControlPanelLift,
    controlPanelTab,
    controlPanelDrawerPlacement,
    serverId,
  } = useSelector((state: RootState) => state.monitoring);
  const { permissions } = useSelector((state: RootState) => state.user);

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [tabActiveKey, setTabActiveKey] = useState<string>("FAULTS");
  const [displayFrontDoors, setDisplayFrontDoors] = useState<boolean>(true);
  const [displayRearDoors, setDisplayRearDoors] = useState<boolean>(true);
  const [server, setServer] = useState<ServerInterface>({ ...BLANK_SERVER });

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const [triggerGetServer] = useLazyGetServerQuery();

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const getServer = async () => {
    const token = await GETJwtToken();
    try {
      const result = await triggerGetServer({
        token,
        idServer: serverId,
      }).unwrap();
      if (result && result.data) {
        setServer(result.data);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  const onClickClose = () => {
    dispatch(updateOpenModalControlPanelLift(false));
    dispatch(updateLiftSelected({ ...BLANK_LIFT }));
    dispatch(updateBankSelected({ ...BLANK_BANK }));
  };

  const generateFrontRearConfiguration = () => {
    let bankHasFrontDoors = false;
    let bankHasRearDoors = false;
    const liftFloors = liftSelected.lift_floors;
    bankSelected.bank_floors.map((floor: any) => {
      // Generate new bank floors configuration
      const findSpecificLiftFloor = liftFloors.find(
        (item) => item.bank_floor_id === floor.id
      );
      if (findSpecificLiftFloor) {
        // Check if has front or rear doors
        if (findSpecificLiftFloor.front_door) {
          bankHasFrontDoors = true;
        }
        if (findSpecificLiftFloor.rear_door) {
          bankHasRearDoors = true;
        }
      }
      return true;
    });
    setDisplayFrontDoors(bankHasFrontDoors);
    setDisplayRearDoors(bankHasRearDoors);
  };
  // ************************************************* */
  // USE EFFECT ************************************** */
  useEffect(() => {
    setTabActiveKey(controlPanelTab.tab);
  }, [controlPanelTab]);

  useEffect(() => {
    if (bankSelected.id !== "" && liftSelected.id !== "") {
      generateFrontRearConfiguration();
    }
  }, [bankSelected]);

  useEffect(() => {
    if (openModalControlPanelLift) {
      getServer();
    }
  }, [openModalControlPanelLift]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <Drawer
      width="1000"
      placement={controlPanelDrawerPlacement}
      closable={false}
      onClose={onClickClose}
      open={openModalControlPanelLift}
      push={false}
      footer={
        <div style={{ textAlign: "right" }}>
          <ButtonClose onClick={onClickClose} />
        </div>
      }
    >
      <div>
        {/** ************************************************** */}
        {/* HEADER */}
        <div>
          <ControlPanelLiftHeader server={server} />
        </div>
        <br />
        <div>
          {/** ************************************************** */}
          {/* BODY */}
          <Tabs
            onTabClick={(key: string) => {
              setTabActiveKey(key);
            }}
            activeKey={tabActiveKey}
          >
            {controlPanelTab.liftType === "ELEVATOR" && (
              <>
                {validatePermission(
                  "monitor-control-panel-car-calls",
                  permissions
                ) && (
                    <Tabs.TabPane
                      key="CARCALLS"
                      tab="Car Calls"
                      disabled={liftSelected.id === ""}
                    >
                      <ControlPanelLiftElevator
                        hasFrontDoors={displayFrontDoors}
                        hasRearDoors={displayRearDoors}
                      />
                    </Tabs.TabPane>
                  )}
              </>
            )}
            {validatePermission(
              "monitor-control-panel-actions",
              permissions
            ) && (
                <Tabs.TabPane key="ACTIONS" tab="Actions">
                  <ControlPanelLiftActions
                    isControlPanel
                    bankId=""
                    bankName=""
                  />
                </Tabs.TabPane>
              )}
            {validatePermission(
              "monitor-control-panel-faults",
              permissions
            ) && (
                <Tabs.TabPane key="FAULTS" tab="Faults">
                  <ControlPanelLiftFaults />
                </Tabs.TabPane>
              )}
            {controlPanelTab.liftType === "ELEVATOR" && (
              <>
                {validatePermission(
                  "monitor-control-panel-status",
                  permissions
                ) && (
                    <Tabs.TabPane key="STATUS" tab="Status">
                      <ControlPanelLiftStatus bank={bankSelected} />
                    </Tabs.TabPane>
                  )}
              </>
            )}
            {validatePermission(
              "monitor-control-panel-faults",
              permissions
            ) && (
                <Tabs.TabPane key="STATE" tab="State">
                  <ControlPanelStateView liftId={liftSelected.id} />
                </Tabs.TabPane>
              )}
          </Tabs>
        </div>
      </div>
    </Drawer>
  );
}
